// Shows a mail signature on the app's dark background without losing what
// makes it that person's signature: its fonts, sizes, bold and colours.
//
// A signature is written for white paper. On dark, navy or black text
// disappears, so a colour that would be too dark to read is lightened: same
// hue, more light. Colours that already read well are left exactly as they
// are. A part with its own background (a coloured table cell) keeps it, and
// text on it is checked against that background instead.
//
// Preview only: the mail that is sent carries the signature unchanged.

export const DARK_BACKGROUND = [32, 32, 35]; // the preview box, as rgb
const LIGHT_TEXT = [212, 212, 216]; // zinc-300
const DARK_TEXT = [24, 24, 27]; // zinc-900
const READABLE = 4.5; // WCAG contrast for normal text

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB =
  /^rgba?\(\s*(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)[\s,]+(\d+(?:\.\d+)?)(?:[\s,/]+([\d.]+%?))?\s*\)$/i;

/** [r, g, b] for a CSS colour written as hex or rgb(); null for anything else. */
export function parseColor(value) {
  const text = (value || '').trim();
  const hex = HEX.exec(text);
  if (hex) {
    const digits =
      hex[1].length === 3
        ? hex[1]
            .split('')
            .map((d) => d + d)
            .join('')
        : hex[1];
    return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
  }
  const rgb = RGB.exec(text);
  if (!rgb) return null;
  // A fully transparent colour is no colour.
  if (rgb[4] !== undefined && parseFloat(rgb[4]) === 0) return null;
  return [rgb[1], rgb[2], rgb[3]].map((n) => Math.round(Number(n)));
}

const channel = (v) => {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const luminance = ([r, g, b]) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);

export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

const toHsl = ([r, g, b]) => {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return [h / 6, s, l];
};

const fromHsl = ([h, s, l]) => {
  if (s === 0) return [l, l, l].map((v) => Math.round(v * 255));
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const part = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return [part(h + 1 / 3), part(h), part(h - 1 / 3)].map((v) =>
    Math.round(v * 255),
  );
};

/** The same hue, moved towards light or dark until it reads on `background`. */
export function readableOn(color, background) {
  if (contrast(color, background) >= READABLE) return color;
  const lighten = luminance(background) < 0.5;
  const [h, s, l] = toHsl(color);
  for (let step = 1; step <= 40; step += 1) {
    const next = lighten ? l + ((1 - l) * step) / 40 : l - (l * step) / 40;
    const candidate = fromHsl([h, s, next]);
    if (contrast(candidate, background) >= READABLE) return candidate;
  }
  return lighten ? LIGHT_TEXT : DARK_TEXT;
}

const css = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;

function adapt(element, background, inheritedText) {
  let ground = background;
  let text = inheritedText;

  const own = parseColor(element.style.backgroundColor);
  if (own) {
    ground = own;
    // Text that had no colour of its own was black on this background.
    text = readableOn(DARK_TEXT, ground);
    if (!parseColor(element.style.color)) element.style.color = css(text);
  } else if (element.style.backgroundColor) {
    // A background we cannot read (a keyword, a gradient): drop it, as the
    // box is dark and we cannot tell what would be readable on it.
    element.style.backgroundColor = '';
  }

  const colour = parseColor(element.style.color);
  if (colour) {
    text = readableOn(colour, ground);
    element.style.color = css(text);
  } else if (element.style.color) {
    element.style.color = '';
  }
  // The old <font color="..."> tag, still written by some signatures.
  const legacy = parseColor(element.getAttribute('color'));
  if (legacy) element.setAttribute('color', css(readableOn(legacy, ground)));

  Array.from(element.children).forEach((child) => adapt(child, ground, text));
}

/** Signature HTML adjusted to read on the dark preview box. */
export function signatureForDark(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(
    `<div id="signature-root">${html}</div>`,
    'text/html',
  );
  const root = doc.getElementById('signature-root');
  Array.from(root.children).forEach((child) =>
    adapt(child, DARK_BACKGROUND, LIGHT_TEXT),
  );
  return root.innerHTML;
}
